import { CrossCircledIcon } from "@radix-ui/react-icons";
import { CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem } from "../select/select";
import { cn } from "@/lib/utils";
import { ReactElement } from "react";
import { PopoverAlignment, PopoverDirection } from "../popover/popover";

export type StatusType = "success" | "failure";

export interface StatusOption {
  id: string;
  label: string;
  type: StatusType;
}

export interface StatusPopupProps {
  value: StatusOption["id"];
  options: StatusOption[];
  onValueChange: (id: StatusOption["id"]) => void;
  trigger: ReactElement;
  popupOptions?: {
    open?: boolean;
    setOpen?: (open: boolean) => void;
    direction?: PopoverDirection;
    align?: PopoverAlignment;
  };
}

interface StatusTypeConfig {
  icon: React.ReactNode;
  btnClass: string;
  borderClass: string;
  selectedClass: string;
  hoverClass: string;
  bgMutedClass: string;
  txtClass: string;
}

export const statusTypeConfig: Record<StatusType, StatusTypeConfig> = {
  success: {
    icon: <CheckCircle2 className="h-5 w-5 text-success" />,
    btnClass: "bg-success",
    borderClass: "border-success",
    selectedClass: "bg-success text-white [&_svg]:text-white",
    hoverClass:
      "hover:bg-success hover:text-white hover:border-success [&_svg]:hover:text-white",
    bgMutedClass: "bg-success-muted",
    txtClass: "text-success",
  },
  failure: {
    icon: <CrossCircledIcon className="h-5 w-5 text-destructive" />,
    btnClass: "bg-destructive",
    borderClass: "border-destructive",
    selectedClass: "bg-destructive text-white [&_svg]:text-white",
    hoverClass:
      "hover:bg-destructive hover:text-white hover:border-destructive [&_svg]:hover:text-white",
    bgMutedClass: "bg-destructive-muted",
    txtClass: "text-destructive",
  },
};

export function StatusPopup({
  options,
  trigger,
  value,
  onValueChange,
  popupOptions,
}: StatusPopupProps) {
  const statusType: Record<StatusType, number> = { failure: 0, success: 1 };
  const sortedOptions = options.sort(
    (a, b) => statusType[b.type] - statusType[a.type]
  );

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      open={popupOptions?.open}
      onOpenChange={popupOptions?.setOpen}
    >
      {trigger}
      <SelectContent side={popupOptions?.direction} align={popupOptions?.align}>
        <div className="flex flex-col gap-2 p-1 ">
          <SelectItem value="-" className="hidden" />
          {sortedOptions.map(({ id, type, label }) => (
            <SelectItem key={id} value={id} asChild>
              <div
                className={cn(
                  "flex items-center gap-2 p-2 pr-6 border rounded-md cursor-pointer",
                  statusTypeConfig[type].borderClass,
                  statusTypeConfig[type].hoverClass,
                  id === value && statusTypeConfig[type].selectedClass
                )}
              >
                {statusTypeConfig[type].icon}
                <p className="font-semibold">{label}</p>
              </div>
            </SelectItem>
          ))}
        </div>
      </SelectContent>
    </Select>
  );
}
